import { formatDateLabel, formatDealWindow, formatShortDate, formatTimeRange, firstSentence } from "@/components/local-buzz/format";
import type { BuzzBusiness, BuzzDeal, BuzzEvent, BuzzLockedTeaser, BuzzMagazine } from "@/components/local-buzz/types";
import type { LockedContentTeaser } from "@/lib/member-content";
import type { Business, BusinessCategory, BusinessSubcategory, Deal, DealTypeRow, EventCategory, EventRow, Magazine, Suburb } from "@/lib/supabase/types";

function locked(teaser: LockedContentTeaser): BuzzLockedTeaser {
  return {
    entityType: teaser.entityType,
    id: teaser.id,
    slug: teaser.slug,
    title: teaser.title,
    imageUrl: teaser.imageUrl
  };
}

export function mapLockedEvent(teaser: LockedContentTeaser): BuzzEvent {
  return {
    id: teaser.id,
    slug: teaser.slug,
    title: teaser.title,
    tagline: "Members-only local event.",
    category: "Members only",
    suburb: "Sydney",
    dateLabel: "Date protected",
    dateShort: "Locked",
    timeLabel: "Sign in",
    venue: "Members only",
    address: "Sign in to view",
    priceLabel: "Members",
    bookingNote: "Sign in",
    description: "Sign in to see this event.",
    imageUrl: teaser.imageUrl,
    websiteUrl: null,
    tags: [],
    startsAt: null,
    endsAt: null,
    lockedTeaser: locked(teaser)
  };
}

export function mapEventToBuzz({
  event,
  category,
  suburb
}: {
  event: EventRow;
  category?: EventCategory | null;
  suburb?: Suburb | null;
  index?: number;
}): BuzzEvent {
  const suburbName = suburb?.name ?? event.suburb ?? "Sydney";
  return {
    id: event.id,
    slug: event.slug,
    title: event.name,
    tagline: firstSentence(event.description, `${category?.name ?? "Event"} in ${suburbName}.`),
    category: category?.name ?? event.tags[0] ?? "Event",
    suburb: suburbName,
    dateLabel: formatDateLabel(event.start_at),
    dateShort: formatShortDate(event.start_at),
    timeLabel: formatTimeRange(event.start_at, event.end_at),
    venue: event.venue_name ?? "Venue TBA",
    address: event.venue_address ?? suburbName,
    priceLabel: event.ticket_price_info ?? "Details",
    bookingNote: event.how_to_book ?? (event.website_url ? "Website" : "Details"),
    description: event.description ?? "Neighbourhood Voice local event.",
    imageUrl: event.image_url,
    websiteUrl: event.website_url,
    tags: event.tags,
    startsAt: event.start_at,
    endsAt: event.end_at,
    hostName: event.organiser_name ?? event.entered_by
  };
}

export function mapLockedDeal(teaser: LockedContentTeaser): BuzzDeal {
  return {
    id: teaser.id,
    slug: teaser.slug,
    title: teaser.title,
    merchant: "Members only",
    type: "deal",
    category: "Members only",
    suburb: "Sydney",
    offer: "Sign in to view",
    description: "Sign in to see this deal.",
    daysAvailable: "Protected",
    priceFrom: null,
    websiteUrl: null,
    phone: null,
    address: null,
    imageUrl: teaser.imageUrl,
    voucherUrl: null,
    voucherCode: null,
    startsAt: null,
    endsAt: null,
    tags: [],
    lockedTeaser: locked(teaser)
  };
}

export type DealWithBusiness = Deal & {
  businesses: Business | null;
  deal_types?: DealTypeRow | null;
};

export function mapDealToBuzz({
  deal,
  type,
  business,
  suburb
}: {
  deal: Deal;
  type?: DealTypeRow | null;
  business?: Business | null;
  suburb?: Suburb | null;
  index?: number;
}): BuzzDeal {
  const businessName = business?.business_name ?? "Neighbourhood Voice";
  const typeName = type?.name ?? "Deal";
  return {
    id: deal.id,
    slug: deal.slug,
    title: deal.title,
    merchant: businessName,
    type: /intro/i.test(typeName) ? "intro-offer" : "deal",
    category: typeName,
    suburb: suburb?.name ?? "Sydney",
    offer: deal.offer_text,
    description: deal.description ?? deal.short_description ?? business?.description ?? "Local offer from a neighbourhood business.",
    daysAvailable: formatDealWindow(deal.start_at, deal.expires_at),
    priceFrom: deal.offer_text.match(/\$[\d,.]+|free/i)?.[0] ?? null,
    websiteUrl: deal.website_url ?? business?.website_url ?? null,
    phone: business?.mobile ?? null,
    address: [business?.address_line_1, business?.address_line_2].filter(Boolean).join(", ") || null,
    imageUrl: deal.image_url ?? business?.cover_image_url ?? business?.logo_url ?? null,
    voucherUrl: deal.voucher_download_url,
    voucherCode: deal.coupon_code,
    startsAt: deal.start_at,
    endsAt: deal.expires_at,
    tags: [typeName, ...(business?.tags ?? [])]
  };
}

export function mapLockedBusiness(teaser: LockedContentTeaser): BuzzBusiness {
  return {
    id: teaser.id,
    slug: teaser.slug,
    name: teaser.title,
    category: "Members only",
    subcategory: null,
    suburb: "Sydney",
    phone: null,
    email: null,
    websiteUrl: null,
    instagramUrl: null,
    address: null,
    description: "Sign in to see this business.",
    imageUrl: teaser.imageUrl,
    tags: [],
    sponsor: false,
    lockedTeaser: locked(teaser)
  };
}

export function mapBusinessToBuzz({
  business,
  category,
  subcategory,
  suburb
}: {
  business: Business;
  category?: BusinessCategory | null;
  subcategory?: BusinessSubcategory | null;
  suburb?: Suburb | null;
}): BuzzBusiness {
  return {
    id: business.id,
    slug: business.slug,
    name: business.business_name,
    category: category?.name ?? "Business",
    subcategory: subcategory?.name ?? null,
    suburb: suburb?.name ?? "Sydney",
    phone: business.mobile,
    email: business.email,
    websiteUrl: business.website_url,
    instagramUrl: business.instagram_url,
    address: [business.address_line_1, business.address_line_2].filter(Boolean).join(", ") || null,
    description: business.description ?? `Discover this ${subcategory?.name ?? category?.name ?? "local"} business in the NV directory.`,
    imageUrl: business.cover_image_url ?? business.logo_url,
    tags: business.tags,
    sponsor: business.sponsor
  };
}

export function mapMagazineToBuzz(magazine: Magazine): BuzzMagazine {
  return {
    id: magazine.id,
    slug: magazine.slug,
    title: magazine.title,
    month: magazine.month,
    year: magazine.year,
    feature: magazine.feature,
    description: magazine.description,
    coverImageUrl: magazine.cover_image_url,
    pdfUrl: magazine.pdf_url,
    editionNumber: magazine.edition_number
  };
}
